import React, { useState } from 'react';
import useSeo from '../seo';

const MODULES = [
  {
    n: 1, name: 'Site-Level OPEX', tag: 'The Foundation',
    desc: 'The universal core. It builds budgets from "Price × Quantity" operational drivers rather than generic GL averages — so every line is defensible.',
    uses: [['Retail', 'cleaning, electricity and floor staff per square meter.'], ['Construction', 'site security, fencing and equipment rental per project phase.'], ['Manufacturing', 'factory overheads and utility usage per production line.']],
  },
  {
    n: 2, name: 'Supply Chain', tag: 'Input Costing',
    desc: 'Calculates the specific cost of getting inputs to the site — from raw materials to a factory or cement to a build.',
    uses: [['Retail', 'tracks landed cost from port to central warehouse.'], ['Construction', 'tracks haulage costs for raw materials to site.']],
  },
  {
    n: 3, name: 'Production & Transformation', tag: 'The "Make" Module',
    desc: 'Manages the conversion of inputs into outputs — Bills of Materials, direct labour, yield loss, and comprehensive shift planning and costing.',
    uses: [['Manufacturing', 'complex BOMs, production stages, packaging, and shift planning.'], ['Construction', 'becomes project phase costing against a Bill of Quantities.'], ['Retail', 'typically deactivated, or used for private-label kitting.']],
  },
  {
    n: 4, name: 'Distribution & Fulfillment', tag: 'The "Move" Module',
    desc: 'Budgets for warehousing, distribution centres, forklifts, picking crews, and detailed shift planning and costing.',
    uses: [['Distribution', 'critical — the heart of the cost structure (DC throughput, shelving, shift costing).'], ['Manufacturing', 'finished-goods storage and route-to-market logistics.'], ['Mining', 'tracks movement of extracted resources and heavy vehicle logistics.']],
  },
  {
    n: 5, name: 'CAPEX', tag: 'The Growth Module',
    desc: 'A dedicated environment for budgeting new projects and asset acquisition — separate from, but linked to, operational cash flow.',
    uses: [['Universal', 'manage expansion: new stores, new factories, new fleets, heavy equipment.']],
  },
  {
    n: 6, name: 'Revenue & Commercial Implosion', tag: 'The "Profit" Module',
    desc: 'The strategic engine. It aggregates costs from every active module to calculate a bottom-up break-even price.',
    uses: [['Retail', 'sets shelf prices from the full cost of buying, storing and staffing.'], ['Construction', '"implodes" all site and material costs to set the tender/bid price.'], ['Manufacturing', 'sets the distributor list price on full supply-chain recovery.']],
  },
  {
    n: 7, name: 'Enterprise Visibility', tag: 'The "View" Module',
    desc: 'The reporting layer. Granular site-level reporting for local managers, rolled up to country and group level for the C-suite.',
    uses: [['Universal', 'one source of truth from the shop floor to the top floor.']],
  },
  {
    n: 8, name: 'People & Paycost', tag: 'The HR Module',
    desc: 'A secure paycost budget housing containing all organizational structures and grades. Paycost allows you to accurately predict people costs, overheads, and union rate impacts.',
    uses: [['Universal', 'Manage headcount, grades, and predictive labor costs securely across all active sites.']],
  },
  {
    n: 9, name: 'Demand & Network Planning', tag: 'The "Plan" Module',
    desc: 'Models the physical network before the money moves through it — which sites feed which, what volume travels down each lane, and what that flow costs. The planning layer that turns a demand forecast into a costed distribution plan.',
    uses: [['Distribution', 'maps the site network and lane volumes that drive inter-depot rating.'], ['Manufacturing', 'balances production volumes against where the demand actually sits.'], ['Retail', 'plans replenishment flow from DC to store before the budget is locked.']],
  }
];

const AI_USES = [
  {
    icon: '🧠',
    title: 'Large Language Models — build and shape the budget',
    body: 'Talk to your budget in plain language. "Take last year’s costs and add 5%" or "flex the Cape Town shift pattern to a six-day week" — the LLM populates and reshapes the lines with you, instead of you keying them in cell by cell.',
    chip: 'Budget population',
  },
  {
    icon: '📈',
    title: 'Machine Learning — forecast your volumes',
    body: 'Volume drives everything downstream in a Price × Quantity model. ML learns from your own sales history, seasonality and actuals to propose volumes by SKU, site and month — which your team then accepts, adjusts or overrides.',
    chip: 'Volume forecasting',
  },
  {
    icon: '🔢',
    title: 'Regression — optimise shifts and logistics',
    body: 'Regression models search your labour and distribution data for the least-cost answer: shift patterns that cover demand without stacking up overtime, and logistics plans that move the same volume for less.',
    chip: 'Cost optimisation',
  },
];

const Platform = () => {
  const [active, setActive] = useState(MODULES[0]);

  useSeo('/platform');

  return (
    <>
    <section className="mn-section" id="platform">
      <div className="mn-container">
        <div className="mn-section-head">
          <span className="mn-kicker">Build Your Own Business Model</span>
          <h1>One composable platform. Nine interoperable modules.</h1>
          <p>
            Every client starts with the base modules and activates complexity as needed — keeping
            the software lean, relevant and cost-effective.
          </p>
        </div>

        <div className="mn-modules">
          <div className="mn-mod-list" role="tablist">
            {MODULES.map((m) => (
              <button
                key={m.n}
                role="tab"
                aria-selected={active.n === m.n}
                className={`mn-mod-tab ${active.n === m.n ? 'is-active' : ''}`}
                onClick={() => setActive(m)}
              >
                <span className="mn-mod-num">{m.n}</span>
                <span>
                  <span className="mn-mod-tab-name">{m.name}</span><br />
                  <span className="mn-mod-tab-tag">{m.tag}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="mn-mod-detail" role="tabpanel">
            <span className="mn-mod-chip">Module {active.n} · {active.tag}</span>
            <h3>{active.name}</h3>
            <p>{active.desc}</p>
            <h4>How it adapts</h4>
            <ul className="mn-mod-uses">
              {active.uses.map(([who, what]) => (
                <li key={who}><span><b>{who}:</b> {what}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* ------------------------------------------------------------- AI --- */}
    <section className="mn-section mn-section-alt" id="ai">
      <div className="mn-container">
        <div className="mn-section-head">
          <span className="mn-kicker">AI Inside the Model</span>
          <h2>Three kinds of AI, each doing the job it is actually good at.</h2>
          <p>
            AI in Mantle is not a chatbot bolted onto the side of a budget. Each technique is applied
            where it genuinely beats a person working through a spreadsheet — and every output stays
            yours to accept, adjust or reject.
          </p>
        </div>
        <div className="mn-grid-3">
          {AI_USES.map((a) => (
            <div className="mn-card" key={a.title}>
              <div className="mn-card-icon" aria-hidden="true">{a.icon}</div>
              <h3>{a.title}</h3>
              <p>{a.body}</p>
              <span className="mn-config">{a.chip}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
    </>
  );
};

export default Platform;